import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Bell } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useNotify } from "@/context/NotifyContext";
import { track } from "@/lib/analytics";
import { LOGOS } from "@/lib/assets";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function NotifyMeModal() {
  const { t } = useLanguage();
  const { open, source, closeNotify } = useNotify();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [newsletter, setNewsletter] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setDone(false);
      setError("");
      track("notify_modal_opened", { source });
    }
  }, [open, source]);

  // Lock body scroll while open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeNotify(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeNotify]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError(t("notify.errors.email"));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim() || null,
          newsletter_opt_in: newsletter,
          source: source || "notify-modal",
        }),
      });
      if (!res.ok) throw new Error("waitlist_failed");
      setDone(true);
      track("notify_modal_submitted", { source });
    } catch {
      setError(t("notify.errors.network"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="notify-overlay"
          data-testid="notify-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notify-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={closeNotify}
        >
          <motion.div
            className="notify-card"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="notify-close"
              aria-label={t("notify.close")}
              onClick={closeNotify}
              className="notify-close"
            >
              <X size={18} strokeWidth={1.75} />
            </button>

            {!done ? (
              <>
                <div className="notify-header">
                  <img
                    src={LOGOS.star}
                    alt=""
                    className="notify-logo feaststar-logo"
                    aria-hidden="true"
                    draggable="false"
                  />
                  <span className="notify-eyebrow">{t("notify.eyebrow")}</span>
                </div>
                <h3 id="notify-title" className="notify-title">
                  <span className="block">{t("notify.h1a")}</span>
                  <span className="block italic">{t("notify.h1b")}</span>
                </h3>
                <p className="notify-sub">{t("notify.sub")}</p>

                <form onSubmit={onSubmit} className="notify-form" data-testid="notify-form">
                  <label className="notify-label">
                    <span>{t("notify.emailLabel")}</span>
                    <input
                      type="email"
                      required
                      data-testid="notify-email"
                      placeholder={t("notify.emailPlaceholder")}
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      className="field-input"
                      autoFocus
                    />
                  </label>
                  <label className="notify-label">
                    <span>
                      {t("notify.phoneLabel")} <em className="notify-optional">{t("notify.optional")}</em>
                    </span>
                    <input
                      type="tel"
                      data-testid="notify-phone"
                      placeholder={t("notify.phonePlaceholder")}
                      value={phone}
                      onChange={(ev) => setPhone(ev.target.value)}
                      className="field-input"
                    />
                  </label>

                  {error && (
                    <div className="notify-error" data-testid="notify-error">{error}</div>
                  )}

                  <label className="notify-check" data-testid="notify-newsletter-label">
                    <input
                      type="checkbox"
                      checked={newsletter}
                      onChange={(ev) => setNewsletter(ev.target.checked)}
                      data-testid="notify-newsletter"
                    />
                    <span className="notify-check-box" aria-hidden="true">
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span className="notify-check-text">{t("notify.newsletterLabel")}</span>
                  </label>

                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="notify-submit"
                    className="btn-primary notify-submit"
                  >
                    <Bell size={16} strokeWidth={1.75} />
                    {busy ? t("notify.submitting") : t("notify.cta")}
                  </button>

                  <p className="notify-privacy">{t("notify.privacy")}</p>
                </form>
              </>
            ) : (
              <div className="notify-done" data-testid="notify-success">
                <div className="notify-done-icon"><Check size={28} strokeWidth={2} /></div>
                <h3 className="notify-title"><span className="block italic">{t("notify.successTitle")}</span></h3>
                <p className="notify-sub">{t("notify.successBody")}</p>
                <button
                  type="button"
                  data-testid="notify-success-close"
                  onClick={closeNotify}
                  className="btn-ghost notify-submit"
                >
                  {t("notify.successClose")}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
